import type { StackProps } from "@mui/material";
import { Link, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

export interface PageStatusProps extends Omit<StackProps, "title"> {
  title: string;
  description: string;
  linkText?: string;
  linkHref?: string;
  /**
   * Text previous to the link, for example: "If you want to go back to the home page, "
   * This text will be displayed before the linkText.
   * @default "Si deseas regresar al inicio, "
   */
  linkDescription?: string;
  onLinkClick?: () => void;
  icon?: ReactNode;
}

export function PageStatus({
  title,
  description,
  linkText,
  linkHref,
  linkDescription: linkPrefix = "Si deseas regresar al inicio, ",
  onLinkClick,
  icon,
  ...props
}: PageStatusProps) {
  return (
    <Stack
      sx={{
        minHeight: "100vh",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: 3, md: 6 },
      }}
      {...props}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        gap={3}
        sx={{
          alignItems: "center",
          maxWidth: 800,
        }}
      >
        <Stack gap={5} sx={{ flex: 1, maxWidth: 520, order: { xs: 2, md: 1 } }}>
          <Typography variant="h6" color="primary.main">
            {title}
          </Typography>

          <Typography variant="body1">{description}</Typography>

          {linkText && (
            <Typography variant="body1">
              <span>{linkPrefix}</span>
              <Link
                href={linkHref}
                onClick={onLinkClick}
                sx={(theme) => ({
                  color: theme.palette.primary.dark,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                })}
              >
                {linkText}
              </Link>
            </Typography>
          )}
        </Stack>

        {icon && (
          <Stack
            sx={{
              width: "fit-content",
              flexShrink: 0,
              order: { xs: 1, md: 2 },
            }}
          >
            {icon}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
